import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useCornTechnologies } from '/@/hooks/catalog';
import { markAllNotificationsAsRead, markAsClicked, markAsRead, useNotifications } from '/@/hooks/notifications';
import { getAppInstance, getSessionCountry } from '/@/store/session/selectors';
import { trackEvent } from '/@/store/tracking';
import { addDays } from '/@/utils';

interface Notification {
    id: number;
    createdAt: string;
    readedOn?: string;
    appIds: number[];
    countryIds: number[];
    [key: string]: unknown;
}

export interface UseIconNotificationReturn {
    // State
    anchorEl: HTMLElement | null;
    unreadedNotifications: Notification[];
    readedNotifications: Notification[];
    open: boolean;
    id: string | undefined;

    // Computed values
    hasVTPRO4Technology: boolean;
    shouldShowNotificationIcon: boolean;
    showWarningCard: boolean;

    // Handlers
    handleClick: (event: React.MouseEvent<HTMLElement>) => void;
    handleClose: () => void;
    dismissWarning: () => void;
    markNotificationAsRead: (id: number) => Promise<void>;
    markNotificationAsClicked: (id: number) => Promise<void>;
    markAllAsRead: () => Promise<void>;
}

/**
 * Custom hook for managing notification icon state and behavior
 */
export const useIconNotification = (): UseIconNotificationReturn => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [unreadedNotifications, setUnreadedNotifications] = useState<Notification[]>([]);
    const [readedNotifications, setReadedNotifications] = useState<Notification[]>([]);
    // Persisted across sessions so the warning card never auto-opens again once dismissed.
    const [warningDismissed, setWarningDismissed] = useState<boolean>(
        () => localStorage.getItem('readed-warning') === 'true'
    );

    const dispatch = useDispatch();
    const appInstance = useSelector(getAppInstance);
    const country = useSelector(getSessionCountry);
    const technologiesQuery = useCornTechnologies({ page: 1, pageSize: 10000 });
    const technologies = (technologiesQuery.data?.results || []).filter((t) => t.displayInCatalog);

    const { data: apiNotifications } = useNotifications({ country: country });

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    // Memoize expensive computations
    const hasVTPRO4Technology = useMemo(() => {
        return technologies.some((tech) => tech.name === 'VTPRO4');
    }, [technologies]);

    const shouldShowNotificationIcon = useMemo(() => {
        return (hasVTPRO4Technology && country.code === 'AR') || appInstance.enableNotifications;
    }, [hasVTPRO4Technology, country.code, appInstance.enableNotifications]);

    const shouldShowWarningPopover = useMemo(() => {
        return hasVTPRO4Technology && country.code === 'AR' && !appInstance.enableNotifications;
    }, [hasVTPRO4Technology, country.code, appInstance.enableNotifications]);

    // The standalone warning card auto-opens for eligible users until they dismiss it,
    // and can be reopened afterwards by clicking the notification bell (`open`).
    const showWarningCard = shouldShowWarningPopover && (open || !warningDismissed);

    // Memoize filtered notifications to avoid unnecessary recalculations
    const { filteredUnreadedNotifications, filteredReadedNotifications } = useMemo(() => {
        if (!apiNotifications) {
            return { filteredUnreadedNotifications: [], filteredReadedNotifications: [] };
        }

        const limitDate = addDays(new Date(), -15); // Won't show notifications older than 15 days
        const filteredReadedNotifications = apiNotifications.readedNotifications.filter((n) => {
            const notificationDate = new Date(n.createdAt);
            return notificationDate > limitDate;
        });

        return {
            filteredUnreadedNotifications: apiNotifications.unreadedNotifications as Notification[],
            filteredReadedNotifications,
        };
    }, [apiNotifications]);

    // Event handlers
    const dismissWarning = useCallback(() => {
        dispatch(trackEvent({ key: 'notifications.vtPro4' }));
        localStorage.setItem('readed-warning', 'true');
        setWarningDismissed(true);
        setAnchorEl(null); // also clear bell-triggered open so the card hides reliably
    }, [dispatch]);

    const markNotificationAsRead = useCallback(
        async (id: number) => {
            dispatch(trackEvent({ key: 'notifications.markAsRead' }));
            await markAsRead({ id, country });
        },
        [dispatch, country]
    );

    const markNotificationAsClicked = useCallback(
        async (id: number) => {
            dispatch(trackEvent({ key: 'notifications.markAsClicked' }));
            await markAsClicked({ id, country });
        },
        [dispatch, country]
    );

    const markAllAsRead = useCallback(async () => {
        dispatch(trackEvent({ key: 'notifications.markAsRead', type: 'all' }));
        await markAllNotificationsAsRead({ unreadedNotifications, country });
    }, [dispatch, unreadedNotifications, country]);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
        void markAllAsRead();
    }, [markAllAsRead]);

    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            dispatch(trackEvent({ key: 'notifications.iconClick' }));
            setAnchorEl(event.currentTarget);
        },
        [dispatch]
    );

    // Effects
    useEffect(() => {
        setUnreadedNotifications(filteredUnreadedNotifications);
        setReadedNotifications(filteredReadedNotifications);
    }, [filteredUnreadedNotifications, filteredReadedNotifications]);

    const [searchParams] = useSearchParams();
    const provider = searchParams.get('provider');
    const isFieldviewPopoverOpen = provider === 'fieldview';

    return {
        // State
        anchorEl,
        unreadedNotifications,
        readedNotifications,
        open: open && !isFieldviewPopoverOpen,
        id,

        // Computed values
        hasVTPRO4Technology,
        shouldShowNotificationIcon,
        showWarningCard,

        // Handlers
        handleClick,
        handleClose,
        dismissWarning,
        markNotificationAsRead,
        markNotificationAsClicked,
        markAllAsRead,
    };
};

export default useIconNotification;
